import {
  DEFAULT_FOLLOW_UP_PHASES,
  type FollowUpPhaseDefinition,
  NOTIFICATION_TYPE_LABELS,
  type NotificationType,
  ORGANIZATION_CSV_FIELD_LABELS,
  ORGANIZATION_CSV_FIELDS,
  ORGANIZATION_CSV_HEADER_ALIASES,
  SKILL_SCORE_OPTIONS,
  TRAINING_SKILL_ITEM_CSV_FIELD_LABELS,
  TRAINING_SKILL_ITEM_CSV_FIELDS,
  TRAINING_SKILL_ITEM_CSV_HEADER_ALIASES,
  type TrainingEnrollmentStatus,
} from '@lms/shared'
import {
  BarChart3,
  Bell,
  Building2,
  CalendarCheck,
  ClipboardCheck,
  ClipboardList,
  ListChecks,
  MessageSquare,
  Settings,
  UserCheck,
  UserRound,
  Workflow,
} from 'lucide-react'
import { Fragment, type ReactNode } from 'react'
import {
  ManualCallout,
  ManualList,
  ManualSection,
  type ManualSectionMeta,
  ManualSteps,
  ManualSubheading,
  ManualTable,
  UiLabel,
} from '@/components/manual/ManualBlocks'
import { StatusBadge } from '@/components/training/StatusBadge'
import { Separator } from '@/components/ui/separator'

// ─── 画面の定数から組み立てる説明（画面の表記とずれないようにする） ───

function describePhase(phase: FollowUpPhaseDefinition, index: number): string {
  const base = index === 0 ? '研修の最終日' : '前の回の期日'
  const months = phase.dueAfterMonths > 0 ? `${phase.dueAfterMonths}か月` : ''
  const days = phase.dueAfterDays > 0 ? `${phase.dueAfterDays}日` : ''
  return `期日は${base}の${months}${days}後（当日を含む）。期日の${phase.openDaysBeforeDue}日前から回答できます`
}

function StatusList({ statuses }: { statuses: TrainingEnrollmentStatus[] }) {
  return (
    <span className="inline-flex flex-wrap gap-1 align-middle">
      {statuses.map((status) => (
        <StatusBadge key={status} type="enrollment" status={status} />
      ))}
    </span>
  )
}

/** 通知の宛先ときっかけ（通知タイプの追加漏れを型で検出するため Record にする） */
const NOTIFICATION_TRIGGERS: Record<NotificationType, { recipient: string; trigger: string }> = {
  enrollment_confirmation: { recipient: '受講者', trigger: '研修に申し込んだとき' },
  approval_request: { recipient: '直属上司', trigger: '承認が必要な申込があったとき' },
  enrollment_approved: { recipient: '受講者', trigger: '申込が承認されたとき' },
  enrollment_rejected: { recipient: '受講者', trigger: '申込が却下されたとき' },
  assignment_notice: { recipient: '受講者', trigger: '研修にアサインされたとき' },
  assignment_supervisor_notice: {
    recipient: '直属上司',
    trigger: '部下が研修にアサインされたとき',
  },
  training_updated: {
    recipient: '「確定」の受講者',
    trigger: '研修内容を更新したとき（定員を設定した研修のみ）',
  },
  followup_opened: { recipient: '受講者', trigger: 'スキルチェックの回答開始日（毎日の定時処理）' },
  followup_submitted: { recipient: '上司評価者', trigger: '受講者が本人評価を提出したとき' },
  followup_commented: { recipient: 'コメントの相手', trigger: '評価シートにコメントしたとき' },
  followup_evaluation_returned: {
    recipient: '差し戻された評価者',
    trigger: '人事が評価を差し戻したとき',
  },
  reminder_followup: {
    recipient: '受講者',
    trigger: '期日を過ぎても本人評価が未提出の間、毎日',
  },
  reminder_followup_supervisor: {
    recipient: '上司評価者',
    trigger: '期日を過ぎても上司評価が未提出の間、毎日',
  },
  reminder_approval: { recipient: '承認者', trigger: '承認期限を過ぎても未処理の間、毎日' },
  reminder_survey: {
    recipient: 'アンケート作成時点の受講者全員（回答状況は連携されません）',
    trigger: 'アンケート管理の「リマインダー送信」',
  },
  escalation: {
    recipient: '人事担当者（テナント研修設定の「デフォルト問い合わせメール」）',
    trigger: '承認期限の2倍を過ぎても未処理の申込がある間、毎日',
  },
}

// ─── 各章 ─────────────────────────────────────────

interface TrainingManualChapter extends ManualSectionMeta {
  body: ReactNode
}

const overview: TrainingManualChapter = {
  id: 'training-overview',
  title: '研修管理の全体像',
  icon: Workflow,
  body: (
    <>
      <p>
        研修管理では、集合研修の申込・承認・出欠から、受講後のスキルチェック（本人評価・上司評価）の回収、
        集計までを行います。操作は講師メニューの <UiLabel>研修管理</UiLabel>{' '}
        などから行い、受講者と上司は画面上部の <UiLabel>研修一覧</UiLabel>{' '}
        <UiLabel>マイ受講</UiLabel> <UiLabel>承認待ち</UiLabel> から操作します。
      </p>
      <ManualTable
        headers={['役割', '主な操作']}
        rows={[
          [
            '人事（管理者）',
            '組織マスターの取込、研修設定・通知テンプレート、スキルチェック項目の登録、評価の差し戻し、上司評価者の個別指定、スキルチェック集計CSVの出力',
          ],
          [
            '研修運営（講師）',
            '研修の作成・公開、受講者のアサイン、出欠の記録、KPIレポート、アンケート',
          ],
          ['受講者', '研修への申込、本人評価の提出'],
          ['上司', '部下の申込の承認、上司評価の提出'],
        ]}
      />
      <ManualCallout tone="warning" title="人事（管理者）だけが行える操作">
        <p>
          講師メニューには全項目が表示されますが、<UiLabel>組織マスター</UiLabel>{' '}
          <UiLabel>通知テンプレート</UiLabel> <UiLabel>研修設定</UiLabel>{' '}
          <UiLabel>スキルチェック項目</UiLabel>{' '}
          と、評価シートの閲覧・差し戻しは管理者権限が必要です。講師権限では画面を開いても操作時にエラーになります。
        </p>
      </ManualCallout>
      <div>
        <ManualSubheading>研修の流れ</ManualSubheading>
        <ManualSteps
          steps={[
            <>
              <strong>組織マスターを取り込む</strong>（承認者・上司評価者の判定に使います）
            </>,
            <strong key="create">研修を作成し、スキルチェック項目を登録する</strong>,
            <>
              <strong>研修を公開して申込を受け付ける</strong>、またはアサインする
            </>,
            <>
              <strong>上司が申込を承認する</strong>（承認必須の設定のとき）
            </>,
            <>
              <strong>研修当日に出欠を記録する</strong>
              。全日程に出席すると受講完了になり、スキルチェックが始まります
            </>,
            <>
              <strong>受講者が本人評価、上司が上司評価を提出する</strong>
              （受講直後と受講3か月後など）
            </>,
            <strong key="report">KPIレポートとスキルチェック集計CSVで結果を確認する</strong>,
          ]}
        />
      </div>
    </>
  ),
}

const initialSetup: TrainingManualChapter = {
  id: 'training-setup',
  title: '初期設定（研修設定・通知テンプレート）',
  icon: Settings,
  body: (
    <>
      <p>
        運用を始める前に、講師メニューの <UiLabel>研修設定</UiLabel>（テナント研修設定）と{' '}
        <UiLabel>通知テンプレート</UiLabel> を設定します。どちらも管理者のみ操作できます。
      </p>
      <div>
        <ManualSubheading>テナント研修設定</ManualSubheading>
        <ManualTable
          headers={['設定', '内容']}
          rows={[
            [
              '通知設定',
              'デフォルト通知チャネル、Teams 通知の Webhook URL（Power Automate のトリガー URL）、デフォルト問い合わせメール。Webhook URL が未設定の場合、Teams 通知は送信されません。デフォルト問い合わせメールは、承認期限超過のエスカレーション通知の送信先です（LMS のユーザーとして登録されているメールアドレスを指定します）',
            ],
            [
              'フォローアップ（スキルチェック）設定',
              'スキルチェックの回（名称・期日・回答開始）。1〜10回まで設定できます',
            ],
            ['承認設定', '募集型研修に承認を必須にするか、承認期限（日数）'],
            ['リマインダー設定', 'リマインダーの有効／無効と送信時刻（0〜23時）'],
          ]}
        />
        <p className="mt-3 text-sm">既定のスキルチェックの回は次のとおりです。</p>
        <ManualList
          items={DEFAULT_FOLLOW_UP_PHASES.map((phase, index) => (
            <>
              <strong>{phase.label}</strong>：{describePhase(phase, index)}
            </>
          ))}
        />
        <ManualCallout tone="info">
          <p>
            回の設定は、受講完了の時点の設定で各受講者に作成されます。設定を変更しても、作成済みのスキルチェックの期日は変わりません。
          </p>
        </ManualCallout>
      </div>
      <div>
        <ManualSubheading>通知テンプレート</ManualSubheading>
        <ManualSteps
          steps={[
            <>
              <UiLabel>新規作成</UiLabel> を押します
            </>,
            <>
              <UiLabel>通知タイプ</UiLabel> を選び、<UiLabel>件名</UiLabel> と{' '}
              <UiLabel>本文</UiLabel> を入力します。本文では <code>{'{{trainingTitle}}'}</code>{' '}
              のような変数が使え、改行には <code>{'<br>'}</code> が使えます
            </>,
            <>保存します。通知タイプごとに1つのテンプレートを登録できます</>,
          ]}
        />
        <ManualCallout tone="warning" title="テンプレートが無い通知は送信されません">
          <p>
            送りたい通知は、すべて事前にテンプレートを登録してください。通知の種類は「通知」の章を参照してください。
          </p>
        </ManualCallout>
      </div>
    </>
  ),
}

const organizationMaster: TrainingManualChapter = {
  id: 'organization-master',
  title: '組織マスターの取込',
  icon: Building2,
  body: (
    <>
      <p>
        組織マスターは、申込の承認者（直属上司）とスキルチェックの上司評価者を決めるために使います。人事システムから出力した
        CSV をそのまま取り込めます。講師メニューの <UiLabel>組織マスター</UiLabel>{' '}
        から操作します（管理者のみ）。
      </p>
      <ManualSteps
        steps={[
          <>
            <UiLabel>CSVファイルを選択</UiLabel>{' '}
            でファイルを選びます。カンマ区切り・タブ区切りのどちらでもよく、文字コードは UTF-8 /
            Shift_JIS に対応しています
          </>,
          <>
            <UiLabel>プレビュー</UiLabel>{' '}
            を押すと、追加・更新・変更なし・エラーの件数と、取り込まれる内容が表示されます。この時点ではまだ登録されません
          </>,
          <>
            内容を確認して <UiLabel>インポート実行</UiLabel> → <UiLabel>実行</UiLabel>{' '}
            を押します。やり直す場合は <UiLabel>やり直す</UiLabel> を押します
          </>,
          <>結果（対象行・追加・更新・仮ユーザー作成・スキップ）を確認します</>,
        ]}
      />
      <div>
        <ManualSubheading>CSVの列</ManualSubheading>
        <p className="mb-3 text-sm">
          <strong>社員コード</strong>と<strong>社員メールアドレス</strong>
          が必須です。見出しは次のいずれの表記でも受け付けます。
        </p>
        <ManualTable
          headers={['列', '受け付ける見出し']}
          rows={ORGANIZATION_CSV_FIELDS.map((field) => [
            ORGANIZATION_CSV_FIELD_LABELS[field],
            ORGANIZATION_CSV_HEADER_ALIASES[field].join(' / '),
          ])}
        />
      </div>
      <div>
        <ManualSubheading>取込のルール</ManualSubheading>
        <ManualList
          items={[
            '1ファイル 1MB・5000行までです',
            'メールアドレスで既存のメンバーと照合し、追加または上書きします。社員コード・部署・役職・直属上司のいずれかが変わった社員は「更新」になります。CSV に含まれないメンバーは削除されません',
            'まだ LMS にログインしていない社員は、メールアドレスで仮ユーザーを作成します（プレビューでは「新規」と表示）。初回ログイン時に同じアカウントに紐付きます',
            '直属上司が CSV の社員行に無い場合は、直属上司メールアドレスで上司もメンバーとして登録します（プレビューでは「上司」と表示）。その上司が既に組織マスターに登録されている場合は、部署・役職・上司を上書きしません',
            '直属上司コードに該当する社員がおらず、直属上司メールアドレスも空の場合は、上司なしとして登録し警告を表示します',
            '社員コード・メールアドレスが空の行や重複した行はエラーとして取り込みません（エラーは「行 N」で表示。見出しが1行目です）',
          ]}
        />
      </div>
      <ManualCallout tone="tip" title="異動があったとき">
        <p>
          最新の CSV
          を取り込み直すと、上司の紐付けも更新されます。スキルチェックの上司評価者は、評価する時点の組織マスター上の直属上司です。
        </p>
      </ManualCallout>
    </>
  ),
}

const trainingCreation: TrainingManualChapter = {
  id: 'training-create',
  title: '研修の作成と公開',
  icon: ClipboardList,
  body: (
    <>
      <p>
        講師メニューの <UiLabel>研修管理</UiLabel>（研修マスター管理）で <UiLabel>新規作成</UiLabel>{' '}
        を押して研修を作成します。
      </p>
      <ManualTable
        headers={['項目', '内容']}
        rows={[
          ['タイトル（必須）', '200文字以内'],
          [
            'タイプ（必須）',
            '「募集型」は受講者が自分で申し込みます。「アサイン型」は研修運営がアサインします',
          ],
          [
            '定員',
            '空欄は無制限。却下・キャンセルは定員に数えません。残席が0になると研修一覧・詳細に「満席」と表示され、申し込めなくなります',
          ],
          ['説明・カテゴリ・標準受講時間・問い合わせメール', '研修詳細画面に表示されます'],
          [
            '通知チャネル',
            '「なし」の場合はテナント研修設定の既定チャネルを使います。「メール（準備中）」は選択できません',
          ],
          ['日程', '「日程を追加」で Day ごとに開始日時・終了日時・場所を入力します'],
        ]}
      />
      <ManualCallout tone="warning" title="日程は作成時にだけ入力できます">
        <p>
          編集画面には日程の欄がありません。作成時に全日程を入力してください。日程はスキルチェックの期日の起点（研修の最終日）にもなります。
        </p>
      </ManualCallout>
      <div>
        <ManualSubheading>公開</ManualSubheading>
        <p className="text-sm">
          作成した研修は「下書き」です。一覧の編集ボタンから編集画面を開き、公開スイッチを{' '}
          <UiLabel>公開中</UiLabel> にすると、受講者の <UiLabel>研修一覧</UiLabel> に表示されます。
        </p>
      </div>
      <div>
        <ManualSubheading>研修一覧の操作ボタン</ManualSubheading>
        <p className="text-sm">
          各研修の行には、左から「編集」「割当」「出欠管理」「スキルチェック項目」「レポート」「削除」のボタンがあります。
        </p>
      </div>
      <ManualCallout tone="info">
        <p>
          定員を設定した研修を更新すると、「確定」の受講者に「研修内容の変更のお知らせ」が送られます。
        </p>
      </ManualCallout>
    </>
  ),
}

const skillItems: TrainingManualChapter = {
  id: 'skill-items',
  title: 'スキルチェック項目の登録',
  icon: ListChecks,
  body: (
    <>
      <p>
        受講後に本人と上司が評価する項目を、研修ごとに登録します。研修一覧の{' '}
        <UiLabel>スキルチェック項目</UiLabel> ボタンから開きます（管理者のみ）。
      </p>
      <ManualCallout tone="warning" title="項目が無い研修ではスキルチェックが作成されません">
        <p>受講完了より前に項目を登録してください。</p>
      </ManualCallout>
      <div>
        <ManualSubheading>画面で登録する</ManualSubheading>
        <ManualSteps
          steps={[
            <>
              <UiLabel>項目を追加</UiLabel> に「項目コード」（例: LT101）・「大項目」（例:
              ■論理的思考を意識する）・「具体項目」を入力して <UiLabel>追加</UiLabel> を押します
            </>,
            <>
              一覧で大項目・具体項目・表示順を編集したり、<UiLabel>有効</UiLabel>{' '}
              スイッチで項目を無効にしたりできます
            </>,
            <>
              評価の目安（例:「基礎編受講者は6項目目までのクリアが目安」）は{' '}
              <UiLabel>評価シートの補足</UiLabel>{' '}
              に入力します。受講者と上司の評価シートに表示されます
            </>,
          ]}
        />
        <ManualCallout tone="info">
          <p>
            項目コードは、集計で同じ項目として扱うため追加後に変更できません。無効化した項目と同じコードも使えません。
          </p>
        </ManualCallout>
      </div>
      <div>
        <ManualSubheading>CSVで一括登録する</ManualSubheading>
        <p className="mb-3 text-sm">
          <UiLabel>CSVから一括取り込み（任意）</UiLabel>{' '}
          から取り込みます。行の順番が表示順になります（1MB・500行まで）。
        </p>
        <ManualTable
          headers={['列', '受け付ける見出し']}
          rows={TRAINING_SKILL_ITEM_CSV_FIELDS.map((field) => [
            TRAINING_SKILL_ITEM_CSV_FIELD_LABELS[field],
            TRAINING_SKILL_ITEM_CSV_HEADER_ALIASES[field].join(' / '),
          ])}
        />
        <ManualList
          items={[
            'CSV の内容を正として同期します。コードが一致する項目は更新、新しいコードは追加、CSV に無い項目は無効化します（削除はしません）',
            'エラーが1件でもあると、全件取り込みません',
          ]}
        />
      </div>
      <ManualCallout tone="tip" title="評価中の受講者への影響">
        <p>
          受講完了の時点の項目が受講者ごとに保存されます。あとで項目を編集・無効化しても、評価中の受講者の項目は変わりません。
        </p>
      </ManualCallout>
    </>
  ),
}

const enrollment: TrainingManualChapter = {
  id: 'training-enroll',
  title: '申込・承認・アサイン',
  icon: UserCheck,
  body: (
    <>
      <div>
        <ManualSubheading>募集型：受講者の申込と上司の承認</ManualSubheading>
        <ManualSteps
          steps={[
            <>
              受講者が <UiLabel>研修一覧</UiLabel> から研修を開き、
              <UiLabel>この研修に申し込む</UiLabel> を押します
            </>,
            <>
              承認必須の設定では <StatusList statuses={['pending_approval']} />{' '}
              になり、組織マスター上の直属上司に承認依頼が届きます
            </>,
            <>
              上司が画面上部の <UiLabel>承認待ち</UiLabel>（承認一覧）で <UiLabel>承認</UiLabel>{' '}
              または <UiLabel>却下</UiLabel>（理由は任意）を押します
            </>,
            <>
              承認されると <StatusList statuses={['confirmed']} /> になります
            </>,
          ]}
        />
        <ManualList
          items={[
            '承認が不要な設定では、申込と同時に「確定」になります',
            '承認必須の設定で、組織マスターに上司が登録されていない社員は申し込めません',
            '満席の研修（研修一覧・詳細に「満席」と表示）、未公開の研修には申し込めません',
          ]}
        />
      </div>
      <div>
        <ManualSubheading>アサイン型：研修運営によるアサイン</ManualSubheading>
        <ManualSteps
          steps={[
            <>
              研修一覧の <UiLabel>割当</UiLabel> ボタンから受講者アサイン画面を開きます
            </>,
            <>
              ユーザーIDを1行に1つ（またはカンマ区切り）で入力し、<UiLabel>アサイン実行</UiLabel>{' '}
              を押します
            </>,
          ]}
        />
        <ManualList
          items={[
            'アサインした受講者はすぐに「確定」になり、受講者と直属上司にお知らせが届きます',
            '既にアサイン・申込済みのユーザーは重複して登録されません',
          ]}
        />
      </div>
      <div>
        <ManualSubheading>キャンセル</ManualSubheading>
        <p className="text-sm">
          受講者は <UiLabel>マイ受講</UiLabel> の <UiLabel>キャンセル</UiLabel> から取り消せます（{' '}
          <StatusList statuses={['applied', 'pending_approval', 'confirmed']} /> のとき）。
        </p>
      </div>
    </>
  ),
}

const attendance: TrainingManualChapter = {
  id: 'training-attendance',
  title: '出欠管理と受講完了',
  icon: CalendarCheck,
  body: (
    <>
      <p>
        研修一覧の <UiLabel>出欠管理</UiLabel>{' '}
        ボタンから開きます。日程（Day）ごとのタブで、受講者ごとにスイッチを「出席」「欠席」に切り替えます。
      </p>
      <ManualList
        items={[
          <>
            記録できるのは <StatusList statuses={['confirmed', 'in_progress']} />{' '}
            の受講者です。却下・キャンセルの受講者は表示されません
          </>,
          <>
            最初に記録すると <StatusList statuses={['in_progress']} /> になります
          </>,
          <>
            <strong>全日程に「出席」が付くと受講完了</strong>になり、スキルチェックが作成されて{' '}
            <StatusList statuses={['in_follow_up']} /> になります
          </>,
        ]}
      />
      <ManualCallout tone="warning" title="受講完了後は出欠を変更できません">
        <p>全日程の出席を記録する前に、記録内容を確認してください。</p>
      </ManualCallout>
      <ManualCallout tone="info">
        <p>
          スキルチェック項目が1件も無い研修では、全日程に出席しても「完了」のままで、スキルチェックは作成されません。
        </p>
      </ManualCallout>
    </>
  ),
}

const followUp: TrainingManualChapter = {
  id: 'training-follow-up',
  title: 'スキルチェック（フォローアップ）',
  icon: ClipboardCheck,
  body: (
    <>
      <p>
        受講完了後、受講者本人と上司がスキルチェック項目を5段階で評価します。評価シートは次の画面から開きます。
      </p>
      <ManualTable
        headers={['利用者', '開き方']}
        rows={[
          ['受講者', '「マイ受講」の研修カードの「スキルチェック」ボタン'],
          ['上司', '「承認待ち」→「部下の受講状況」（配下メンバー受講状況）の「評価」ボタン'],
          ['人事（管理者）', '研修の「レポート」（KPIレポート）の受講者別一覧の「開く」ボタン'],
        ]}
      />
      <div>
        <ManualSubheading>評価の流れ</ManualSubheading>
        <ManualSteps
          steps={[
            <>回答開始日になると、受講者に「スキルチェックの入力開始」が届きます</>,
            <>
              受講者が全項目を評価して <UiLabel>本人評価を提出</UiLabel>{' '}
              を押します。上司評価者に通知が届きます
            </>,
            <>
              上司が全項目を評価して <UiLabel>上司評価を提出</UiLabel>{' '}
              を押します。本人評価の提出前には上司評価を提出できません
            </>,
            <>本人評価と上司評価がそろうと、その回は「完了」になります</>,
            <>
              全ての回が完了すると、受講のステータスが <StatusList statuses={['closed']} />{' '}
              になります
            </>,
          ]}
        />
      </div>
      <div>
        <ManualSubheading>評価基準</ManualSubheading>
        <ManualTable
          headers={['点数', '基準']}
          rows={SKILL_SCORE_OPTIONS.map(({ score, label }) => [String(score), label])}
        />
      </div>
      <div>
        <ManualSubheading>評価のルール</ManualSubheading>
        <ManualList
          items={[
            '提出後は修正できません。修正が必要な場合は人事に差し戻しを依頼します',
            '上司は、自分の上司評価を提出するまで本人評価の点数を見られません（本人評価を参照せずに評価するため）',
            '受講者は、本人評価と上司評価の両方がそろうと上司評価を見られます',
            '期日（当日を含む）までに提出します。期日を過ぎても提出できます。テナント研修設定でリマインダーが有効な場合、未提出の間は未提出の評価者に毎日リマインドが届きます',
            'コメント欄は受講者と上司が回答開始日以降に使えます（任意。回の完了には影響しません）',
            '1回目の評価が終わったら、研修で設定した行動目標を上司と確認してください',
          ]}
        />
      </div>
      <div>
        <ManualSubheading>人事（管理者）の操作</ManualSubheading>
        <ManualTable
          headers={['操作', '内容']}
          rows={[
            [
              '差し戻し',
              '評価シートの「本人評価を差し戻す」「上司評価を差し戻す」から、理由を入力して差し戻します。評価者に通知が届き、再提出できるようになります',
            ],
            [
              '上司評価者の個別指定',
              '異動直後などで組織マスター上の上司が評価できない場合、「上司評価者の個別指定（人事）」で受講者ごとに評価者を指定します。「組織マスターの上司に戻す」で解除できます',
            ],
          ]}
        />
      </div>
    </>
  ),
}

const report: TrainingManualChapter = {
  id: 'training-report',
  title: 'レポートと集計CSV',
  icon: BarChart3,
  body: (
    <>
      <p>
        研修一覧の <UiLabel>レポート</UiLabel> ボタンから KPIレポートを開きます。
      </p>
      <ManualList
        items={[
          '受講者数・完了率・出席率・アンケート回答率・スキルチェック本人評価の提出率',
          'スキルチェック（受講者別）：フォロー中・クローズの受講者の評価シートを開けます',
        ]}
      />
      <ManualTable
        headers={['ボタン', '出力内容']}
        rows={[
          ['CSVエクスポート', '受講者ごとの氏名・メールアドレス・ステータス・受講開始日・完了日'],
          [
            'スキルチェック集計CSV（管理者のみ）',
            <Fragment key="skill-check-csv">
              1行＝受講者×項目の形式で、社員番号・氏名・研修名・項目ID・項目名と、回ごとの本人評価・上司評価（例:{' '}
              {DEFAULT_FOLLOW_UP_PHASES.flatMap((phase) => [
                `${phase.label}本人`,
                `${phase.label}上司`,
              ]).join('・')}
              ）を出力します。差し戻し中の評価は空欄です
            </Fragment>,
          ],
        ]}
      />
    </>
  ),
}

const survey: TrainingManualChapter = {
  id: 'training-survey',
  title: 'アンケート',
  icon: MessageSquare,
  body: (
    <>
      <p>
        研修アンケートは Microsoft Forms で作成し、その URL を研修に登録します。講師メニューの{' '}
        <UiLabel>研修アンケート</UiLabel> から操作します。
      </p>
      <ManualSteps
        steps={[
          <>
            <UiLabel>アンケート作成</UiLabel> を押し、研修を選んで{' '}
            <UiLabel>Microsoft Forms URL</UiLabel> を入力し、<UiLabel>作成</UiLabel> を押します
          </>,
          <>
            受講完了後、受講者の研修詳細画面に <UiLabel>アンケートに回答する</UiLabel>{' '}
            リンクが表示されます
          </>,
          <>
            回答状況は Microsoft Forms の回答一覧で確認します。催促する場合は{' '}
            <UiLabel>リマインダー送信</UiLabel> を押します（下記の注意を参照）
          </>,
        ]}
      />
      <ManualCallout tone="warning" title="Forms の回答状況は LMS に連携されません">
        <ManualList
          items={[
            'LMS は、誰が Forms に回答したかを把握できません。そのため KPIレポートのアンケート回答率は 0% のままになります',
            '「リマインダー送信」は、アンケート作成時点の受講者全員に届きます。回答済みの人にも届くため、送る場合は文面（通知テンプレート「アンケート未回答のリマインド」）を「未回答の方は」のような表現にしてください',
          ]}
        />
      </ManualCallout>
      <ManualCallout tone="info">
        <p>
          アンケートは1研修につき1つです。対象者は、アンケートを作成した時点の受講者です。受講者が確定してから作成してください。
        </p>
      </ManualCallout>
    </>
  ),
}

const notifications: TrainingManualChapter = {
  id: 'training-notifications',
  title: '通知',
  icon: Bell,
  body: (
    <>
      <p>
        通知は Teams の個人チャット（Power Automate の Flow
        bot）に届きます（メール通知は準備中で、選択できません）。各操作で通知が登録され、5分ごとの定時処理で順番に送信されます。リマインド類（表の「毎日」「回答開始日」の通知）は、テナント研修設定でリマインダーが有効な場合に、送信時刻を過ぎた後に1日1回登録されます。
      </p>
      <ManualTable
        headers={['通知', '宛先', 'きっかけ']}
        rows={(Object.keys(NOTIFICATION_TRIGGERS) as NotificationType[]).map((type) => [
          NOTIFICATION_TYPE_LABELS[type],
          NOTIFICATION_TRIGGERS[type].recipient,
          NOTIFICATION_TRIGGERS[type].trigger,
        ])}
      />
      <ManualCallout tone="warning" title="届かないときの確認点">
        <ManualList
          items={[
            'その通知タイプのテンプレートが登録されているか',
            'テナント研修設定に Teams 通知の Webhook URL が設定されているか',
            'リマインドの場合、リマインダーが有効になっているか',
            'エスカレーションの場合、「デフォルト問い合わせメール」が設定され、LMS のユーザーのメールアドレスになっているか',
          ]}
        />
      </ManualCallout>
    </>
  ),
}

const traineeGuide: TrainingManualChapter = {
  id: 'training-trainee-guide',
  title: '受講者・上司の画面',
  icon: UserRound,
  body: (
    <>
      <ManualTable
        headers={['画面', '利用者', '内容']}
        rows={[
          [
            '研修一覧・研修詳細',
            '受講者',
            '公開中の研修の確認（タイプ・カテゴリでの絞り込み、残席・満席の表示）と申込、アンケートへの回答',
          ],
          [
            'マイ受講',
            '受講者',
            '自分の受講状況（タブ: すべて／受講中／完了／フォロー中）、キャンセル、スキルチェック',
          ],
          ['承認待ち（承認一覧）', '上司', '部下の申込の承認・却下'],
          [
            '部下の受講状況（配下メンバー受講状況）',
            '上司',
            '配下メンバーの受講状況とスキルチェックの評価',
          ],
        ]}
      />
      <div>
        <ManualSubheading>受講ステータス</ManualSubheading>
        <ManualList
          items={[
            <>
              <StatusList statuses={['pending_approval']} />
              ：上司の承認を待っています
            </>,
            <>
              <StatusList statuses={['confirmed']} />
              ：受講が確定しています
            </>,
            <>
              <StatusList statuses={['in_progress']} />
              ：出欠の記録が始まっています
            </>,
            <>
              <StatusList statuses={['completed']} />
              ：全日程に出席しました
            </>,
            <>
              <StatusList statuses={['in_follow_up']} />
              ：スキルチェックの回答中です
            </>,
            <>
              <StatusList statuses={['closed']} />
              ：全てのスキルチェックが完了しました
            </>,
            <>
              <StatusList statuses={['rejected', 'cancelled']} />
              ：申込が却下・キャンセルされました
            </>,
          ]}
        />
      </div>
    </>
  ),
}

const chapters: TrainingManualChapter[] = [
  overview,
  initialSetup,
  organizationMaster,
  trainingCreation,
  skillItems,
  enrollment,
  attendance,
  followUp,
  report,
  survey,
  notifications,
  traineeGuide,
]

/** 目次に載せる研修管理の章 */
export const trainingManualSections: ManualSectionMeta[] = chapters.map(({ id, title, icon }) => ({
  id,
  title,
  icon,
}))

export function TrainingManualContent() {
  return (
    <>
      {chapters.map(({ body, ...section }) => (
        <Fragment key={section.id}>
          <Separator />
          <ManualSection section={section}>{body}</ManualSection>
        </Fragment>
      ))}
    </>
  )
}
